import { ConsultFieldTypeEnum } from 'utils/enums';

const STANDALONE_EXCLUSION = 'LastKnownWellOver24Hours';

const withExtendedWindowPrefix = (options) =>
  options.map((option) =>
    option.value === STANDALONE_EXCLUSION
      ? option
      : {
          ...option,
          summaryLabel: `Last known well >4.5 hours and ${
            option.summaryLabel ?? option.label
          }`,
        },
  );

const iviaFields = {
  thrombolyticRelativeContraindicationRisk: {
    name: 'thrombolyticRelativeContraindicationRisk',
    title: 'Thrombolytic Relative Contraindications',
    type: ConsultFieldTypeEnum.TOGGLE,
    options: [
      {
        value: 'BenefitGreaterThanRisk',
        label: 'Benefit>Risk',
        summaryLabel:
          'Despite the following relative contraindications for IV thrombolysis, I believe the benefits of treatment outweigh the risks.',
      },
      {
        value: 'RiskGreaterThanBenefit',
        label: 'Risk>Benefit',
        summaryLabel:
          'Given the presence of the following relative contraindications for IV thrombolysis, I believe the risks of treatment outweigh the potential benefit.',
      },
    ],
  },
  thrombolyticRelativeContraindicationsPredefined: {
    name: 'thrombolyticRelativeContraindicationsPredefined',
    type: ConsultFieldTypeEnum.CHIP,
    options: [
      { value: 'PreExistingDisability', label: 'Pre-existing disability' },
      {
        value: 'DOAC',
        label: 'DOAC',
        summaryLabel: 'DOAC exposure within the previous 48 hours',
      },
      {
        value: 'AIS3Mos',
        label: 'AIS 3 mos',
        summaryLabel: 'Ischemic stroke within 3 months',
      },
      { value: 'PriorICH', label: 'Prior ICH' },
      {
        value: 'NonCnsTrauma',
        label: 'non-CNS trauma',
        summaryLabel: 'Major non-CNS trauma >14 days but less than 3 months prior',
      },
      {
        value: 'NonCnsSurgery',
        label: 'non-CNS surgery',
        summaryLabel: 'Major non-CNS surgery <10 days prior',
      },
      {
        value: 'GiGuBleeding',
        label: 'GI/GU bleeding',
        summaryLabel: 'GI/GU bleeding within 21 days',
      },
      {
        value: 'IntracranialArterialDissection',
        label: 'Intracranial arterial dissection',
      },
      {
        value: 'IntracranialVascularMalformation',
        label: 'Intracranial vascular malformation',
      },
      { value: 'StemiWithin3Months', label: 'STEMI within 3 months' },
      { value: 'AcutePericarditis', label: 'Acute pericarditis' },
      { value: 'LeftHeartThrombus', label: 'Left heart thrombus' },
      { value: 'SystemicActiveMalignancy', label: 'Systemic active malignancy' },
      { value: 'Pregnancy', label: 'Pregnancy' },
      { value: 'PostPartum', label: 'Post-partum' },
      { value: 'DuralPunctureWithin7Days', label: 'Dural puncture within 7 days' },
      {
        value: 'ArterialPunctureWithin7Days',
        label: 'Arterial puncture within 7 days',
      },
      {
        value: 'TBI',
        label: 'TBI',
        summaryLabel: 'Moderate to severe TBI >14 days but less than 3 months prior',
      },
      {
        value: 'Neurosurgery',
        label: 'Neurosurgery',
        summaryLabel: 'Neurosurgery >14 days but less than 3 months prior',
      },
    ],
  },
  thrombolyticRelativeContraindicationsFreeText: {
    name: 'thrombolyticRelativeContraindicationsFreeText',
    associatedField: 'thrombolyticRelativeContraindicationsPredefined',
    type: ConsultFieldTypeEnum.INPUT,
    multiline: true,
  },
  disabilityDiscussion: {
    name: 'disabilityDiscussion',
    // The prompt shows in the UI (as the field label) but must not appear in the
    // summary, so summaryLabel keeps the summary heading short.
    title:
      'Disability Discussion: “I define disability as not being able to return to work, bathe, toilet, walk, or eat by yourself. Do you feel like you would NOT be able to do any of these with your current deficit?”',
    summaryLabel: 'Disability Discussion',
    type: ConsultFieldTypeEnum.TOGGLE,
    options: [
      {
        value: 'DisablingIVTRecommended',
        label: 'Disabling-IVT recommended',
        summaryLabel:
          'I had an individualized disability discussion with the patient and/or family and based on this discussion we believe the benefits of thrombolytic therapy outweigh the potential risks and alternatives and they wish to proceed with IV thrombolysis at this time.',
      },
      {
        value: 'NonDisablingIVTNotRecommended',
        label: 'Non-disabling IVT NOT recommended',
        summaryLabel:
          'I had an individualized disability discussion with the patient and/or family and based on this discussion we believe the risks of thrombolytic therapy outweigh any potential benefits and they do not wish to proceed with IV thrombolysis at this time.',
      },
    ],
  },
  disabilityDiscussionFreeText: {
    name: 'disabilityDiscussionFreeText',
    associatedField: 'disabilityDiscussion',
    type: ConsultFieldTypeEnum.INPUT,
    multiline: true,
  },
  possibleExtendedWindowThrombolyticCandidatePredefined: {
    name: 'possibleExtendedWindowThrombolyticCandidatePredefined',
    label: 'Possible Extended Window Thrombolytic Candidate',
    type: ConsultFieldTypeEnum.MULTICHIP,
    options: [
      {
        value: 'TimeLastKnownWellTimestamp',
        label: 'Time last known well timestamp',
      },
      { value: 'AgeOver18', label: 'Age >18' },
      { value: 'PrestrokeMRS0To1', label: 'Prestroke mRS 0-1' },
      { value: 'DisablingDeficit', label: 'Disabling deficit' },
      { value: 'TraditionalTNKExclusions', label: 'Traditional TNK Exclusions' },
      { value: 'LVO', label: 'LVO' },
      {
        value: 'PerfusionImagingWithinCriteria',
        label: 'Perfusion imaging within criteria',
      },
      {
        value: 'NoClinicallySignificantHypodensityOnNonContrastCTHead',
        label:
          'No clinically significant hypodensity appreciated on non-contrast CT head',
      },
    ],
  },
  possibleExtendedWindowThrombolyticCandidateFreeText: {
    name: 'possibleExtendedWindowThrombolyticCandidateFreeText',
    associatedField: 'possibleExtendedWindowThrombolyticCandidatePredefined',
    type: ConsultFieldTypeEnum.INPUT,
    multiline: true,
  },
  extendedWindowThrombolyticExclusionsPredefined: {
    name: 'extendedWindowThrombolyticExclusionsPredefined',
    label: 'Extended Window Thrombolytic Exclusion',
    type: ConsultFieldTypeEnum.CHIP,
    options: withExtendedWindowPrefix([
      { value: 'LastKnownWellOver24Hours', label: 'Last known well >24 hours' },
      { value: 'AgeUnder18', label: 'Age <18' },
      { value: 'PreStrokeMRS2OrHigher', label: 'Pre-stroke mRS ≥2' },
      { value: 'LVOWithAccessToIATherapy', label: 'LVO with access to IA therapy' },
      {
        value: 'NoMeasurableIschemicCore',
        label: 'No measurable ischemic penumbra',
        summaryLabel: 'no measurable ischemic penumbra',
      },
      { value: 'UnfavorableIschemicCore', label: 'Unfavorable Ischemic core' },
      { value: 'UnfavorableMismatchRatio', label: 'Unfavorable Mismatch ratio' },
      { value: 'MismatchVolumeUnder10ml', label: 'Mismatch volume < 10 ml' },
      { value: 'MatchedMRIDWIFlairLesion', label: 'Matched MRI-DWI/FLAIR lesion' },
      {
        value: 'AppropriateCTPerfusionImagingUnavailable',
        label: 'Appropriate CT Perfusion imaging unavailable',
      },
      {
        value: 'HypodensityOnCT',
        label: 'hypodensity on CT',
        summaryLabel:
          'Clinically referrable hypodensity on non-contrast CT head.',
      },
    ]),
  },
  extendedWindowThrombolyticExclusionsFreeText: {
    name: 'extendedWindowThrombolyticExclusionsFreeText',
    associatedField: 'extendedWindowThrombolyticExclusionsPredefined',
    type: ConsultFieldTypeEnum.INPUT,
    multiline: true,
  },
};

export default iviaFields;
